import { useMutation } from "@apollo/client";
import {
  DocumentAssociationsInput,
  DocumentType,
  DocumentWithMetadataQuery,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { documentTypeToLabel } from "util/constants";

import { UPSERT_DOCUMENT_WITH_METADATA } from "../queries";
import { DocumentMetadataFormData } from "../types";

type DocumentWithMetadataResult = DocumentWithMetadataQuery["document"];
type DocumentMetadataResult = NonNullable<DocumentWithMetadataResult>["documentMetadata"];

type UseDocumentDetailsFormParams = {
  documentId: string | undefined;
  document: DocumentWithMetadataResult | undefined;
  metadata: DocumentMetadataResult | undefined;
  tenantId: string;
};

export function useDocumentDetailsForm({
  documentId,
  document,
  metadata,
  tenantId,
}: UseDocumentDetailsFormParams) {
  const methods = useForm<DocumentMetadataFormData>({
    mode: "onChange",
  });

  const {
    control,
    reset,
    formState: { isDirty },
  } = methods;

  const [upsertDocumentWithMetadata, { loading: saving }] = useMutation(
    UPSERT_DOCUMENT_WITH_METADATA
  );

  // Reset form when document changes
  useEffect(() => {
    if (document) {
      reset({
        documentTitle: document.title,
        documentType: document.documentType ?? DocumentType.Other,
        dateAuthored: document.authoredAt ?? null,
        description: document.description ?? "",
        facilities:
          document.facilities?.map((facility) => ({
            id: facility.id,
            name: facility.name ?? "",
            customerFacilityId: facility.customerFacilityId ?? null,
            streetAddress1: facility.streetAddress1 ?? null,
            streetAddress2: facility.streetAddress2 ?? null,
            city: facility.city ?? null,
            state: facility.state ?? null,
            zip: facility.zip ?? null,
          })) ?? [],
        documentTags:
          document.documentTags?.map((t) => ({
            id: t.id,
            name: t.name,
            type: t.type,
          })) ?? [],
        documentSubtype: metadata?.documentSubtype ?? "",
        documentNumber: metadata?.documentNumber ?? "",
        relatedDocumentNumber: metadata?.relatedDocumentNumber ?? "",
        revisionNumber: metadata?.revisionNumber ?? "",
        reportingPeriod: metadata?.reportingPeriod ?? "",
        siteName: metadata?.siteName ?? "",
        siteAddressLine1: metadata?.siteAddressLine1 ?? "",
        siteAddressLine2: metadata?.siteAddressLine2 ?? "",
        siteCity: metadata?.siteCity ?? "",
        siteState: metadata?.siteState ?? "",
        siteZipCode: metadata?.siteZipCode ?? "",
        siteEmail: metadata?.siteEmail ?? "",
        mailingName: metadata?.mailingName ?? "",
        mailingAddressLine1: metadata?.mailingAddressLine1 ?? "",
        mailingAddressLine2: metadata?.mailingAddressLine2 ?? "",
        mailingCity: metadata?.mailingCity ?? "",
        mailingState: metadata?.mailingState ?? "",
        mailingZipCode: metadata?.mailingZipCode ?? "",
        mailingEmail: metadata?.mailingEmail ?? "",
        contactName: metadata?.contactName ?? "",
        contactEmail: metadata?.contactEmail ?? "",
        contactPhone: metadata?.contactPhone ?? "",
        contactAddressLine1: metadata?.contactAddressLine1 ?? "",
        contactAddressLine2: metadata?.contactAddressLine2 ?? "",
        contactCity: metadata?.contactCity ?? "",
        contactState: metadata?.contactState ?? "",
        contactZipCode: metadata?.contactZipCode ?? "",
        agency: metadata?.agency ?? "",
        programArea: metadata?.programArea ?? "",
        regulatoryProgram: metadata?.regulatoryProgram ?? "",
        regulatoryCitations: metadata?.regulatoryCitations ?? "",
        regulatoryAuthority: metadata?.regulatoryAuthority ?? "",
        issueDate: metadata?.issueDate ?? null,
        expirationDate: metadata?.expirationDate ?? null,
        effectiveDate: metadata?.effectiveDate ?? null,
        submissionDeadline: metadata?.submissionDeadline ?? null,
        dueDate: metadata?.dueDate ?? null,
        amount: metadata?.amount ?? null,
        currency: metadata?.currency ?? "",
        relatedDocumentIds: metadata?.relatedDocumentIds ?? [],
        supersedes: metadata?.supersedes ?? "",
        summary: metadata?.summary ?? "",
        keywords: metadata?.keywords ?? [],
      });
    }
  }, [document, metadata, reset]);

  const currentDocumentType = methods.watch("documentType");
  const isRCRAManifest = currentDocumentType === DocumentType.RcraManifest;

  const documentTypeOptions = useMemo(
    () =>
      Object.values(DocumentType).map((type) => ({
        value: type,
        display: documentTypeToLabel(type),
      })),
    []
  );

  const save = useCallback(
    async (data: DocumentMetadataFormData): Promise<boolean> => {
      if (!documentId || !document) return false;
      if (!isDirty && !metadata?.needsReview && metadata) return false;

      const associations: DocumentAssociationsInput[] = (data.facilities ?? []).map((f) => ({
        tenantId: tenantId ?? "",
        facilityId: f.id,
      }));

      const initialFacilityIds = document.facilities?.map((f) => f.id) ?? [];
      const newFacilityIds = data.facilities?.map((f) => f.id) ?? [];
      const facilitiesToRemove = initialFacilityIds.filter((id) => !newFacilityIds.includes(id));
      const removeAssociations: DocumentAssociationsInput[] = facilitiesToRemove.map((id) => ({
        tenantId: tenantId ?? "",
        facilityId: id,
      }));

      await upsertDocumentWithMetadata({
        variables: {
          id: documentId,
          document: {
            id: document.id,
            tenantId: document.tenantId,
            title: data.documentTitle,
            documentType: data.documentType,
            authoredAt: data.dateAuthored,
            description: document.description,
            fileExtension: document.fileExtension,
            storageLink: document.storageLink,
            isArchived: false,
            documentTags: data.documentTags?.map((t) => ({
              id: t.id,
              name: t.name,
            })),
          },
          associations,
          removeAssociations,
          metadata: {
            documentType: data.documentType,
            documentSubtype: data.documentSubtype || null,
            documentNumber: data.documentNumber || null,
            relatedDocumentNumber: data.relatedDocumentNumber || null,
            revisionNumber: data.revisionNumber || null,
            reportingPeriod: data.reportingPeriod || null,
            siteName: data.siteName || null,
            siteAddressLine1: data.siteAddressLine1 || null,
            siteAddressLine2: data.siteAddressLine2 || null,
            siteCity: data.siteCity || null,
            siteState: data.siteState || null,
            siteZipCode: data.siteZipCode || null,
            siteEmail: data.siteEmail || null,
            mailingName: data.mailingName || null,
            mailingAddressLine1: data.mailingAddressLine1 || null,
            mailingAddressLine2: data.mailingAddressLine2 || null,
            mailingCity: data.mailingCity || null,
            mailingState: data.mailingState || null,
            mailingZipCode: data.mailingZipCode || null,
            mailingEmail: data.mailingEmail || null,
            contactName: data.contactName || null,
            contactEmail: data.contactEmail || null,
            contactPhone: data.contactPhone || null,
            contactAddressLine1: data.contactAddressLine1 || null,
            contactAddressLine2: data.contactAddressLine2 || null,
            contactCity: data.contactCity || null,
            contactState: data.contactState || null,
            contactZipCode: data.contactZipCode || null,
            agency: data.agency || null,
            programArea: data.programArea || null,
            regulatoryProgram: data.regulatoryProgram || null,
            regulatoryCitations: data.regulatoryCitations || null,
            regulatoryAuthority: data.regulatoryAuthority || null,
            issueDate: data.issueDate,
            expirationDate: data.expirationDate,
            effectiveDate: data.effectiveDate,
            submissionDeadline: data.submissionDeadline,
            dueDate: data.dueDate,
            amount: data.amount,
            currency: data.currency || null,
            lineItems: null,
            relatedDocumentIds: data.relatedDocumentIds ?? [],
            supersedes: data.supersedes || null,
            summary: data.summary || null,
            keywords: data.keywords ?? [],
          },
        },
      });

      return true;
    },
    [documentId, document, isDirty, metadata, tenantId, upsertDocumentWithMetadata]
  );

  return {
    methods,
    control,
    isDirty,
    saving,
    currentDocumentType,
    isRCRAManifest,
    documentTypeOptions,
    save,
  };
}
